<script setup lang="ts">
import type { CommandPaletteItem } from '@nuxt/ui';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getFeatureFlagMeta, useRegisteredFeatureFlags } from '#layers/base/utils/feature-flags/state';
import { useRegisteredFeatureTriggers } from '#layers/base/utils/feature-flags/triggers';

const emit = defineEmits<{
	close: [];
}>();

/** Owned by the page header so the search matches the other settings panels. */
const search = defineModel<string>('search', { required: true });

const { t } = useI18n();

const registeredFeatureFlags = useRegisteredFeatureFlags();
const registeredFeatureTriggers = useRegisteredFeatureTriggers();

const commandPaletteGroups = computed(() => {
	const featureFlagItems = registeredFeatureFlags.value.map((featureFlag) => {
		const meta = getFeatureFlagMeta(featureFlag);

		return {
			id: meta.id,
			label: meta.title,
			description: meta.description,
			value: featureFlag.value,
			onSelect() {
				featureFlag.value = !featureFlag.value;
			},
		} satisfies CommandPaletteItem;
	});

	const featureTriggerItems = registeredFeatureTriggers.value.map((featureTrigger) => ({
		id: featureTrigger.id,
		label: featureTrigger.title,
		description: featureTrigger.description,
		icon: 'lucide:play',
		onSelect() {
			const result = featureTrigger.onTrigger();

			emit('close');

			return result;
		},
	} satisfies CommandPaletteItem));

	return [
		{
			id: 'feature-flags',
			label: t('developer.feature_flags.groups.flags'),
			items: featureFlagItems,
		},
		{
			id: 'feature-triggers',
			label: t('developer.feature_flags.groups.triggers'),
			items: featureTriggerItems,
		},
	].filter((group) => group.items.length > 0);
});
</script>

<template>
	<div class="flex min-h-0 flex-1 flex-col">
		<DCommandPalette
			v-model:search-term="search"
			:model-value="null"
			:input="false"
			:groups="commandPaletteGroups"
			:fuse="{
				fuseOptions: {
					keys: [
						'id',
						'label',
						'description',
					],
				},
			}"
			:ui="{
				root: 'min-h-0 flex-1 p-0',
				// Each group is its own outlined card with a readable heading
				viewport: 'flex flex-col gap-4 divide-y-0 p-0',
				group: 'rounded-lg border border-default p-1 [&>.cp-label]:text-default!',
				label: 'px-2 pt-1.5 pb-1 text-xs font-semibold',
				item: 'items-center gap-4 px-4 py-2',
			}"
		>
			<template #item-trailing="{ item }">
				<DSwitch
					v-if="'value' in item"
					:model-value="item.value"
					size="sm"
				/>
			</template>
		</DCommandPalette>
	</div>
</template>
